/**
 * Fechas que vienen de fuentes que no se ponen de acuerdo en el formato.
 *
 * `pagos_stp_raw.fecha_operacion` es TEXT y trae dos formas: `20251208` (el formato de STP, la
 * mayoría de los renglones hasta dic-2025) y `2025-12-05` (desde entonces). `fecha_creacion` es un
 * `timestamp without time zone` que PostgREST devuelve como `2025-12-08T10:31:00.123456`. Pasar
 * `20251208` por `new Date(s + 'T00:00:00')` da "Invalid Date", que es lo que se veía en la
 * columna Operación de Conciliaciones.
 *
 * Aquí se lee cualquiera de esas formas (y `DD/MM/YYYY`, epoch y timestamps con zona) y, si no se
 * puede, se devuelve `null` para que la pantalla muestre su texto de vacío ("Sin fecha" en cobranza). Puro, para probarlo en node.
 */

function fechaLocal(y: number, m: number, d: number): Date | null {
  const f = new Date(y, m - 1, d);
  // new Date corre el desbordamiento (31 feb → 3 mar): si no coincide, la fecha no existe.
  if (f.getFullYear() !== y || f.getMonth() !== m - 1 || f.getDate() !== d) return null;
  return f;
}

/** Lee una fecha en cualquiera de los formatos conocidos. Fecha sin hora = día local, sin corrimiento. */
export function parseFechaFlexible(valor: string | number | null | undefined): Date | null {
  if (valor == null) return null;
  const s = String(valor).trim();
  if (!s) return null;

  let m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (m) return fechaLocal(+m[1], +m[2], +m[3]);

  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return fechaLocal(+m[1], +m[2], +m[3]);

  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) return fechaLocal(+m[3], +m[2], +m[1]);

  // Epoch en segundos (10 dígitos) o milisegundos (13).
  if (/^\d{10}$/.test(s)) return new Date(Number(s) * 1000);
  if (/^\d{13}$/.test(s)) return new Date(Number(s));

  // Timestamp: `2025-12-08 10:31:00` (espacio) se normaliza a `T`. Sin zona, el estándar lo toma
  // como hora local, que es lo que guarda un `timestamp without time zone`.
  m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)(.*)$/.exec(s);
  if (m) {
    const zona = m[3].trim().replace(/^([+-]\d{2})$/, '$1:00');
    const f = new Date(`${m[1]}T${m[2]}${zona}`);
    return Number.isNaN(f.getTime()) ? null : f;
  }

  return null;
}

const dos = (n: number) => String(n).padStart(2, '0');

/** `YYYY-MM-DD` del día local, o `null` si no hay fecha legible. Para filtrar, ordenar y `<input type="date">`. */
export function aDiaIso(valor: string | number | null | undefined): string | null {
  const f = parseFechaFlexible(valor);
  if (!f) return null;
  return `${f.getFullYear()}-${dos(f.getMonth() + 1)}-${dos(f.getDate())}`;
}
